import {
    Async,
    Deferred,
    EventBus,
    Instance,
    isNull,
    Knot,
    Query,
    Script,
    Service,
} from '@siposdani87/sui-js';
import { app } from '../app';
import { ConfigService } from './configService';

export class GoogleCaptchaService extends Service {
    static readonly inject = ['instances', 'configService'];

    script: Script;
    eventBus: EventBus;
    widgetId: string | null;
    async: Async;

    constructor(
        instances: Instance,
        private configService: ConfigService,
    ) {
        super();

        this.script = instances.script;
        this.eventBus = instances.eventBus;

        this.widgetId = null;

        this.async = new Async(2);
        this.async.on('complete', this._eventComplete.bind(this));
    }

    enter(): void {
        this.eventBus.set('controller.loaded', (opt_dom) => {
            this.widgetId = null;
            if (opt_dom) {
                const captchaKnot = new Query('#captcha', opt_dom).getKnot();
                if (!captchaKnot.isEmpty()) {
                    this.async.parallelFunction(
                        () => {
                            return captchaKnot;
                        },
                        undefined,
                        0,
                    );
                }
            }
        });

        this._loadSDK();
    }

    private _loadSDK() {
        const deferred = new Deferred();
        const languageCode = this.configService.getLocale().split('-', 2)[0];
        this.script
            .load(
                'google-recaptcha',
                'https://www.google.com/recaptcha/api.js',
                {
                    hl: languageCode,
                    render: 'explicit',
                },
                true,
                true,
            )
            .then(
                () => {
                    this.async.parallelFunction(
                        () => {
                            return true;
                        },
                        undefined,
                        1,
                    );
                    deferred.resolve();
                },
                () => {
                    deferred.reject();
                },
            );
        return deferred.promise();
    }

    private _eventComplete(isError: boolean, results: Array<any>): void {
        this.async.setStatus(1, isError, 0, results);
        if (!isError) {
            this.render(results[0], '#user_captcha');
        }
    }

    render(node: Knot, inputId: string): void {
        const type = this.configService.get<string>('recaptcha.type');
        const isInvisible = type === 'invisible';
        const tickboxSiteKey = this.configService.get<string>(
            'recaptcha.tickbox_site_key',
        );
        const invisibleSiteKey = this.configService.get<string>(
            'recaptcha.invisible_site_key',
        );
        const siteKey = isInvisible ? invisibleSiteKey : tickboxSiteKey;
        const size = isInvisible ? 'invisible' : 'normal';
        const badge = isInvisible ? 'bottomright' : 'inline';
        // FIXME: Some users/registration on page load grecaptcha.render does not exists
        (window as any)['grecaptcha'].ready(() => {
            this.widgetId = (window as any)['grecaptcha']['render'](
                node.getNode(),
                {
                    sitekey: siteKey,
                    badge: badge,
                    theme: 'light',
                    size: size,
                    callback: (response: string) => {
                        const input = new Query(inputId).getKnot();
                        input.setAttribute('value', response);
                        input.trigger('change');
                    },
                },
            );
            if (isInvisible) {
                this.execute();
            }
        });
    }

    execute(): void {
        if (!isNull(this.widgetId)) {
            (window as any)['grecaptcha'].ready(() => {
                (window as any)['grecaptcha']['execute'](this.widgetId);
            });
        }
    }

    reset(): void {
        if (!isNull(this.widgetId)) {
            (window as any)['grecaptcha'].ready(() => {
                (window as any)['grecaptcha']['reset'](this.widgetId);
            });
        }
    }
}

export const googleCaptchaService = app.service('googleCaptchaService', GoogleCaptchaService);
